import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../hooks/useAuth'
import authService from '../services/authService'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'
import { 
  User, Mail, Edit3, Save, X, LogOut, Globe, MapPin, FileText, Camera
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Skeleton } from '../components/ui/Skeleton'
import ImageUpload from '../components/molecules/ImageUpload'

const ProfilePage = () => {
  const { user, logout, refreshUserData } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    full_name: '', bio: '', country: '', region: '', profile_image: ''
  })

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        bio: user.bio || '',
        country: user.country || '',
        region: user.region || '',
        profile_image: user.profile_image || ''
      })
    }
  }, [user])

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }))

  const handleSave = async () => {
    try {
      setSaving(true)
      const updates = {}
      Object.keys(formData).forEach(key => {
        if (formData[key] !== (user[key] || '')) updates[key] = formData[key]
      })

      if (!Object.keys(updates).length) {
        toast.success('No changes to save')
        setIsEditing(false)
        return
      }

      const response = await authService.updateProfile(updates)

      try {
        await refreshUserData()
      } catch (refreshError) {
        console.error('Profile refresh error:', refreshError)
      }

      toast.success(response.message || 'Profile updated successfully')
      setIsEditing(false)
    } catch (error) {
      console.error('Profile update error:', error)
      toast.error(error.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        bio: user.bio || '',
        country: user.country || '',
        region: user.region || '',
        profile_image: user.profile_image || ''
      })
    }
    setIsEditing(false)
  }

  const handleImageUpload = async (imageFile) => {
    try {
      const result = await authService.uploadProfileImage(imageFile)
      setFormData(prev => ({ ...prev, profile_image: result.imageUrl }))

      try {
        await refreshUserData()
      } catch (refreshError) {
        console.error('Profile refresh error:', refreshError)
      }

      toast.success(result.message || 'Profile image uploaded successfully')
      if (result.metadata?.compressionRatio) {
        toast.success(`Image optimized: ${result.metadata.compressionRatio}% size reduction`)
      }
    } catch (error) {
      console.error('Image upload error:', error)
      throw error
    }
  }

  const handleImageRemove = () => setFormData(prev => ({ ...prev, profile_image: '' }))

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) logout()
  }

  const profileFields = useMemo(() => [
    { key: 'pi_username', label: 'Pi Username', icon: User, value: user?.pi_username || 'Not available', editable: false, type: 'text' },
    { key: 'full_name', label: 'Full Name', icon: User, value: formData.full_name, editable: true, type: 'text', placeholder: 'Enter your full name' },
    { key: 'bio', label: 'Bio', icon: FileText, value: formData.bio, editable: true, type: 'textarea', placeholder: 'Tell us about yourself' },
    { key: 'country', label: 'Country', icon: Globe, value: formData.country, editable: true, type: 'text', placeholder: 'Enter your country' },
    { key: 'region', label: 'Region/State', icon: MapPin, value: formData.region, editable: true, type: 'text', placeholder: 'Enter your region or state' }
  ], [user, formData])

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, staggerChildren: 0.1 } }
  }

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  }

  if (!user) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 p-4 sm:p-6">
        <div className="w-full max-w-md sm:max-w-2xl lg:max-w-4xl mx-auto space-y-6">
          <Card className="w-full p-4 sm:p-6 backdrop-blur-sm bg-white/80 shadow-xl border-0">
            <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-10 w-24" />
            </div>
            <div className="space-y-4">
              {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 p-4 sm:p-6">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-full sm:max-w-md md:max-w-2xl lg:max-w-4xl mx-auto space-y-6"
      >
        {/* Enhanced Profile Header */}
        <motion.div variants={itemVariants}>
          <Card className="gradient-hero text-white border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-teal/20 to-transparent" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-24 -translate-x-24 blur-2xl" />
            
            <div className="relative p-4 sm:p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-6">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                  <motion.div 
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative group"
                  >
                    <div className="w-28 h-28 bg-gradient-to-br from-white/30 to-white/10 rounded-2xl flex items-center justify-center overflow-hidden shadow-2xl backdrop-blur-sm border border-white/20">
                      {user.profile_image ? (
                        <img 
                          src={user.profile_image} 
                          alt="Profile" 
                          className="w-full h-full object-cover rounded-2xl"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                      ) : null}
                      <User className="w-12 h-12 text-white/80" />
                    </div>
                    <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                  </motion.div>
                  
                  <div className="text-center md:text-left">
                    <motion.h1 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-3xl lg:text-4xl font-bold mb-2 bg-gradient-to-r from-white to-white/80 bg-clip-text"
                    >
                      {user.full_name || user.pi_username || 'Welcome'}
                    </motion.h1>
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-lg opacity-90 font-light"
                    >
                      @{user.pi_username || 'Complete your profile to get started'}
                    </motion.p>
                    {user.bio && (
                      <motion.p 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-sm opacity-75 mt-2 max-w-md"
                      >
                        {user.bio}
                      </motion.p>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/40 backdrop-blur-sm transition-all duration-300 shadow-lg"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Enhanced Profile Information */}
        <motion.div variants={itemVariants}>
          <Card className="border-0 shadow-2xl backdrop-blur-sm bg-white/95">
            <CardHeader className="border-b border-neutral-100 bg-gradient-to-r from-neutral-50/50 to-transparent pb-4 sm:pb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <CardTitle className="text-xl font-bold text-neutral-900 flex items-center gap-3">
                  <div className="p-2 bg-primary-teal/10 rounded-lg">
                    <User className="w-5 h-5 text-primary-teal" />
                  </div>
                  Profile Information
                </CardTitle>
                
                {!isEditing ? (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    className="border-primary-teal/30 text-primary-teal hover:bg-primary-teal hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      disabled={saving}
                      className="border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-all duration-300"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-primary-teal hover:bg-primary-teal/90 shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>

            <CardContent className="p-4 sm:p-6 lg:p-8">
              <div className="space-y-8">
                {/* Enhanced Profile Image Upload */}
                <motion.div variants={itemVariants} className="space-y-4">
                  <label className="flex items-center gap-3 text-lg font-semibold text-neutral-900">
                    <div className="p-2 bg-primary-teal/10 rounded-lg">
                      <User className="w-4 h-4 text-primary-teal" />
                    </div>
                    Profile Image
                  </label>
                  <div className="pl-2 sm:pl-6 lg:pl-9">
                    <ImageUpload
                      currentImage={formData.profile_image}
                      onImageUpload={handleImageUpload}
                      onImageRemove={handleImageRemove}
                      disabled={!isEditing || saving}
                      maxSize={5 * 1024 * 1024}
                    />
                  </div>
                </motion.div>

                {/* Enhanced Profile Fields Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                  {profileFields.map((field, index) => {
                    const IconComponent = field.icon
                    
                    return (
                      <motion.div
                        key={field.key}
                        variants={itemVariants}
                        transition={{ delay: index * 0.05 }}
                        className="space-y-3"
                      >
                        <label className="flex items-center gap-3 text-sm font-semibold text-neutral-900">
                          <div className="p-1.5 bg-primary-teal/10 rounded-md">
                            <IconComponent className="w-4 h-4 text-primary-teal" />
                          </div>
                          {field.label}
                          {!field.editable && (
                            <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-500 rounded-full">
                              Read-only
                            </span>
                          )}
                        </label>
                        
                        <div className="pl-2 sm:pl-6 lg:pl-9">
                          {isEditing && field.editable ? (
                            field.type === 'textarea' ? (
                              <textarea
                                value={field.value}
                                onChange={(e) => handleInputChange(field.key, e.target.value)}
                                placeholder={field.placeholder}
                                rows={3}
                                className="w-full px-2 sm:px-3 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-teal/20 focus:border-primary-teal transition-all duration-300 resize-none bg-white/50 backdrop-blur-sm shadow-sm"
                              />
                            ) : (
                              <Input
                                type={field.type}
                                value={field.value}
                                onChange={(e) => handleInputChange(field.key, e.target.value)}
                                placeholder={field.placeholder}
                                className="focus:ring-primary-teal/20 focus:border-primary-teal transition-all duration-300 bg-white/50 backdrop-blur-sm shadow-sm border-neutral-200 rounded-xl px-2 sm:px-3 py-3"
                              />
                            )
                          ) : (
                            <div className="px-2 sm:px-3 py-3 bg-gradient-to-r from-neutral-50 to-neutral-50/50 border border-neutral-100 rounded-xl text-neutral-900 backdrop-blur-sm shadow-sm">
                              {field.value || (
                                <span className="text-neutral-400 italic text-sm">
                                  {field.editable ? 'Not provided' : 'Not available'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default ProfilePage